import React, {useState, useEffect, useCallback} from 'react';
import {SVGS} from '../../../../assets/images';
import {useAppDispatch, useAppSelector} from '../../../../hooks/hooks';
import {deleteShiftType, getShiftsTypes} from '../../../../features/types';
import {TiminatorType} from '../../../../features/types/types';
import IconTextLarge from '../../../../components/shared/large-icon-text';
import PageWrapperV2 from '../../../../components/shared/page-wrapper-v2';
import PageHeader from '../../../../components/shared/page-header';
import PageBody from '../../../../components/shared/page-body';
import { useIntl } from 'react-intl';
import CreateNewButtonV2 from '../../../../components/shared/create-new-button-v2';
import { useNavigate } from 'react-router-dom';
import TiminatorTableV2 from '../../../../components/shared/table-v2';
import { Lx } from '../../../../i18n/consts';
import { DeleteDialog } from '../../../../components/shared/delete-dialog';
import { NAVIGATION_ROUTES } from '../../../../routes/NavigationRoute';
import { useMediaQuery } from 'react-responsive';
import { useShiftTypesTable } from './hook/useShiftTypesTable';
import { useInfinitePagination } from '../../../../hooks/useInfinitePagination';
import { DeleteIconButton } from '../../../../components/shared/delete-icon-button';
import PageFooter from '../../../../components/shared/page-footer';
import { TypeFilter, DEFAULT_TYPE_FILTER_STATE } from '../type-filter';
import { TypeHeader } from '../type-header';
import { canManageShiftTypes, canSeeShiftTypesSection } from '../../../../features/profile/entityPermissions';
import { getVisibleTypeTabs } from '../../tabs';

export default function ShiftTypes() {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const intl = useIntl();

    const [open, setOpen] = useState(false);
    const [selectedShiftType, setSelectedShiftType] = useState<TiminatorType | null>(null);

    const [selectedStatus, setSelectedStatus] = useState<number>(-1);
    const [search, setSearch] = useState('');
    const isMobile = useMediaQuery({ maxWidth: 1000  });

    const shiftTypesState = useAppSelector(store => store.types.shifts);
    const typesState = useAppSelector(store => store.types);
    const profile = useAppSelector(store => store.profile);
    const hasMore = shiftTypesState.items.length < shiftTypesState.total;
    const canEditShiftTypes = canManageShiftTypes(profile, shiftTypesState.permissions);
    const canViewShiftTypes = canSeeShiftTypesSection(profile, shiftTypesState.permissions);

    const fetchShiftTypesPage = useCallback((page: number) => {
        return dispatch(getShiftsTypes(page)).unwrap();
    }, [dispatch]);

    const { loadInitial, loadNext, isLoadingMore, isLoadingInitial, reset } = useInfinitePagination({
        hasMore,
        fetchPage: fetchShiftTypesPage,
    });

    const [mobileView, setMobileView] = useState<'list' | 'filter'>('list');

    const handleMobileFilterClick = () => {
        setMobileView(prev => (prev === 'list' ? 'filter' : 'list'));
    };

    const handleMobileFilterClose = useCallback(() => {
        setMobileView('list');
    }, []);

    const handleFilterChange = useCallback(({ search: nextSearch, selectedStatus: nextStatus }: { search: string; selectedStatus: number }) => {
        setSearch(nextSearch);
        setSelectedStatus(nextStatus);
    }, []);

    const handleMobileHeaderClose = useCallback(() => {
        handleFilterChange(DEFAULT_TYPE_FILTER_STATE);
    }, [handleFilterChange]);

    useEffect(() => {
        if (!canViewShiftTypes) {
            const availableTab = getVisibleTypeTabs(profile, {
                project: typesState.projects.permissions,
                shift: shiftTypesState.permissions,
                absence: typesState.absences.permissions,
                tag: typesState.tags.permissions,
            })[0];

            if (availableTab) {
                navigate(availableTab.path, { replace: true });
            }
        }
    }, [canViewShiftTypes, navigate, profile, shiftTypesState.permissions, typesState.absences.permissions, typesState.projects.permissions, typesState.tags.permissions]);

    useEffect(() => {
        if (!canViewShiftTypes) {
            return;
        }

        loadInitial().catch(() => { /* handled elsewhere */ });
    }, [canViewShiftTypes, loadInitial]);

    useEffect(() => {
        if (!isMobile) {
            setMobileView('list');
        }
    }, [isMobile]);

    const { columns, data, isEmpty } = useShiftTypesTable(
        search,
        selectedStatus,
        isMobile
    );

    if (!canViewShiftTypes) {
        return null;
    }

    const showEmptyState = !isLoadingInitial && isEmpty;

    const enhancedColumns = columns
        .filter(col => canEditShiftTypes || col.id !== 'deleteButton')
        .map(col => {
            if (col.id === 'deleteButton') {
                return {
                    ...col,
                    format: (rowData: TiminatorType) => (
                        <DeleteIconButton
                            onClick={() => {
                                setSelectedShiftType(rowData);
                                setOpen(true);
                            }}
                        />
                    ),
                };
            }
            return col;
        });

    function onRowEdit(type: TiminatorType) {
        if (!canEditShiftTypes) {
            return;
        }

        navigate(NAVIGATION_ROUTES.EDIT_SHIFT_TYPE, {
            state: { shiftType: type }
        });
    }

    const handleDeleteCancel = () => {
        setOpen(false);
        setSelectedShiftType(null);
    };

    const handleDeleteConfirm = () => {
        if (selectedShiftType) {
            dispatch(deleteShiftType(selectedShiftType.id)).unwrap().then(() => {
                reset();
                loadInitial().catch(() => { /* handled elsewhere */ });
            });
        }
        handleDeleteCancel();
    };

    const handleCreateNew = () => {
        if (!canEditShiftTypes) {
            return;
        }

        navigate(NAVIGATION_ROUTES.CREATE_SHIFT_TYPE);
    }

    return <>
        <PageWrapperV2>
            <PageHeader>
                <TypeHeader
                    isEmpty={showEmptyState}
                    isFilterOpen={mobileView === 'filter'}
                    onMobileFilterClick={handleMobileFilterClick}
                    onCreateNew={handleCreateNew}
                    canCreate={canEditShiftTypes}
                    onMobileFilterClose={handleMobileHeaderClose}
                    loading={isLoadingInitial}
                />
            </PageHeader>
            <PageBody justifyContent={showEmptyState ? 'center' : 'flex-start'} alignItems='stretch'>
                {   
                    !showEmptyState
                        ? (
                            <TypeFilter
                                search={search}
                                selectedStatus={selectedStatus}
                                isMobile={isMobile}
                                mobileView={mobileView}
                                onFilterChange={handleFilterChange}
                                onMobileClose={handleMobileFilterClose}
                            >
                                <TiminatorTableV2 
                                    onRowClick={canEditShiftTypes ? onRowEdit : undefined} 
                                    columns={enhancedColumns}
                                    data={data}
                                    hasMore={hasMore}
                                    onLoadMore={loadNext}
                                    isLoadingMore={isLoadingMore} />
                            </TypeFilter>
                        )
                        : <IconTextLarge 
                            text={intl.formatMessage({id: Lx.Type.NO_SHIFT_TYPES})} 
                            subtext={intl.formatMessage({id: Lx.Type.CREATE_AND_USE_SHIFT_TYPE})} 
                            icon={<SVGS.TypesIcon />} />
                }
            </PageBody>
            { 
                isMobile && mobileView === 'list' && canEditShiftTypes && 
                    <PageFooter>
                        <CreateNewButtonV2 onClick={handleCreateNew} />
                    </PageFooter>
            }
        </PageWrapperV2>
        <DeleteDialog 
            open={open} 
            onCancel={handleDeleteCancel} 
            onConfirmDelete={handleDeleteConfirm}
            deleteHeader={intl.formatMessage({id: Lx.Type.DELETE_SHIFT_TYPE})}
            deleteMessage={intl.formatMessage({id: Lx.Type.DELETE_SHIFT_TYPE_MESSAGE}, { name: selectedShiftType?.name })}
            title={intl.formatMessage({id: Lx.General.SHIFT_TYPE})} />
    </>
}
